import { useEffect, useMemo } from "react";
import { SessionProductTree } from "@financial-advisory/data-access/api-types";
import {
  SHARED_SEND_MESSAGE_CACHE_KEY,
  useGetProductTreesQuery,
  useLazyGetSessionProductTreesQuery,
  useSendMessageMutation,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { useChatMessageState } from "@financial-advisory/ui/components/chat";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Outcome, PaneType } from "@unlikelyai-magic/shared/data-access/api";
import { LayoutFlow, LeafNodeValue } from "../components/ReactFlow";

export const ProductTreesPage = () => {
  const { data: productTrees } = useGetProductTreesQuery(null);
  const { sessionId } = useChatMessageState();
  const [
    fetchSessionProductTrees,
    { data: sessionProductTrees, reset: resetSessionProductTrees },
  ] = useLazyGetSessionProductTreesQuery();

  const [, { data: sendMessageResponse }] = useSendMessageMutation({
    fixedCacheKey: SHARED_SEND_MESSAGE_CACHE_KEY,
  });

  const leafNodeValues: LeafNodeValue[] = useMemo(() => {
    // Reset leaf nodes when sessionId is empty (chat reset)
    if (!sessionId || !sessionProductTrees) {
      return [];
    }
    return sessionProductTrees.data.reduce(
      (acc: LeafNodeValue[], tree: SessionProductTree) => {
        return [
          ...acc,
          ...tree.leaf_nodes.map(
            (node): LeafNodeValue => ({
              productTreeId: tree.product_tree_id,
              id: node.name,
              value: node.value ?? Outcome.UNDECIDED,
            })
          ),
        ];
      },
      []
    );
  }, [sessionId, sessionProductTrees]);

  useEffect(() => {
    if (!sessionId) {
      resetSessionProductTrees();
      return;
    }
    if (!sendMessageResponse?.message) return;
    const versionId = sendMessageResponse?.session_version_id ?? "";
    fetchSessionProductTrees({ sessionVersionId: versionId });
  }, [
    fetchSessionProductTrees,
    sessionId,
    sendMessageResponse?.message,
    sendMessageResponse?.session_version_id,
    resetSessionProductTrees,
  ]);

  if (!productTrees) return null;

  return (
    <ReactFlowProvider>
      <LayoutFlow
        panes={productTrees.data.map((tree) => {
          return {
            paneType: PaneType.DEFINITION,
            definition: {
              rootNode: tree.template,
              name: tree.name,
              description: tree.description,
            },
            id: tree.id,
            explanation: tree.description,
            excerpt: tree.description,
          };
        })}
        value={leafNodeValues}
      />
    </ReactFlowProvider>
  );
};
